import React from 'react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { exactWhite, lightenHexHsl, stringToColour } from 'utils/style/colors';

import HeatChart from 'components/HeatChart';
import { isANumber, convertNumbersToProperFormat } from '../../utils/number';
import { ReportOverride } from './ChartViewer';
import { ComposedChartWithStyle } from './styles';
import {
  ICheckPoint,
  IReportView,
  ISeries,
  ISurveyViewWithData,
} from 'store/survey/surveyTypes';
import { IAnalyticsQuery } from 'store/entity/IAnalyticsQuery';
import i18n from '../../i18n';
import messages from './Messages';
import IranMapWithBadge from '../map/IranMap';
import { getQuestionListInfo } from './QuestionListReport';

const marginForChart = {
  top: 5,
  right: 5,
  bottom: 5,
  left: 0,
};

type ComponentProps = {
  calculateOffset: (data: any[]) => number;
  checkpoints: ICheckPoint[];
  currSeries: ISeries;
  CustomizedAxisTick: any; // ReactNode and ReactElement does not work :(
  data: any[];
  handleShowCheckPoint: () => void;
  handleShowRelatedResponsesModal: (
    queryKey: string | null,
    validSecondId?: boolean
  ) => (itemId?: number | any, itemId2?: number | any) => void;
  isPrinting: boolean;
  onChangeShowCheckPoints?: () => void;
  queryKey: string;
  seriesItems: { [key: string]: string[] };
  showCheckPoints: boolean;
  reportTitle: string;
  YAxisLabel: string[];
  groupId: string | undefined;
  itemKey: string | undefined;
  query: IAnalyticsQuery;
  bucket: any;
  compact?: boolean;
  compactAutoDim?: [number, number];
  reportOverride?: ReportOverride;
  ref?: React.Ref<HTMLElement>;
  report?: IReportView;
  simple?: boolean;
  survey: ISurveyViewWithData;
};

function getBarChartHeight(data: any[]) {
  if (data.length === 0) return 100;
  return (
    40 *
    data.length *
    Math.max(
      Object.keys(data[0]).filter((it) => !it.startsWith('__')).length,
      1
    )
  );
}
const getRectanglePath = (
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number[] | number
) => {
  const maxRadius = Math.min(Math.abs(width) / 2, Math.abs(height) / 2);
  const ySign = height >= 0 ? 1 : -1;
  const xSign = width >= 0 ? 1 : -1;
  const clockWise =
    (height >= 0 && width >= 0) || (height < 0 && width < 0) ? 1 : 0;
  let path;

  if (maxRadius > 0 && radius instanceof Array) {
    const newRadius = [];

    for (let i = 0, len = 4; i < len; i++) {
      newRadius[i] = radius[i] > maxRadius ? maxRadius : radius[i];
    }

    path = 'M'.concat(String(x), ',').concat(String(y + ySign * newRadius[0]));

    if (newRadius[0] > 0) {
      path += 'A '
        .concat(String(newRadius[0]), ',')
        .concat(newRadius[0].toString(), ',0,0,')
        .concat(String(clockWise), ',')
        .concat(String(x + xSign * newRadius[0]), ',')
        .concat(String(y));
    }

    path += 'L '
      .concat(String(x + width - xSign * newRadius[1]), ',')
      .concat(String(y));

    if (newRadius[1] > 0) {
      path += 'A '
        .concat(newRadius[1].toString(), ',')
        .concat(newRadius[1].toString(), ',0,0,')
        .concat(String(clockWise), ',\n        ')
        .concat(String(x + width), ',')
        .concat(String(y + ySign * newRadius[1]));
    }

    path += 'L '
      .concat(String(x + width), ',')
      .concat(String(y + height - ySign * newRadius[2]));

    if (newRadius[2] > 0) {
      path += 'A '
        .concat(newRadius[2].toString(), ',')
        .concat(newRadius[2].toString(), ',0,0,')
        .concat(String(clockWise), ',\n        ')
        .concat(String(x + width - xSign * newRadius[2]), ',')
        .concat(String(y + height));
    }

    path += 'L '
      .concat(String(x + xSign * newRadius[3]), ',')
      .concat(String(y + height));

    if (newRadius[3] > 0) {
      path += 'A '
        .concat(newRadius[3].toString(), ',')
        .concat(newRadius[3].toString(), ',0,0,')
        .concat(String(clockWise), ',\n        ')
        .concat(String(x), ',')
        .concat(String(y + height - ySign * newRadius[3]));
    }

    path += 'Z';
  } else if (maxRadius > 0 && radius === +radius && radius > 0) {
    const _newRadius = Math.min(maxRadius, radius);

    path = 'M '
      .concat(String(x), ',')
      .concat(String(y + ySign * _newRadius), '\n            A ')
      .concat(String(_newRadius), ',')
      .concat(String(_newRadius), ',0,0,')
      .concat(String(clockWise), ',')
      .concat(String(x + xSign * _newRadius), ',')
      .concat(String(y), '\n            L ')
      .concat(String(x + width - xSign * _newRadius), ',')
      .concat(String(y), '\n            A ')
      .concat(String(_newRadius), ',')
      .concat(String(_newRadius), ',0,0,')
      .concat(String(clockWise), ',')
      .concat(String(x + width), ',')
      .concat(String(y + ySign * _newRadius), '\n            L ')
      .concat(String(x + width), ',')
      .concat(String(y + height - ySign * _newRadius), '\n            A ')
      .concat(String(_newRadius), ',')
      .concat(String(_newRadius), ',0,0,')
      .concat(String(clockWise), ',')
      .concat(String(x + width - xSign * _newRadius), ',')
      .concat(String(y + height), '\n            L ')
      .concat(String(x + xSign * _newRadius), ',')
      .concat(String(y + height), '\n            A ')
      .concat(String(_newRadius), ',')
      .concat(String(_newRadius), ',0,0,')
      .concat(String(clockWise), ',')
      .concat(String(x), ',')
      .concat(String(y + height - ySign * _newRadius), ' Z');
  } else {
    path = 'M '
      .concat(String(x), ',')
      .concat(String(y), ' h ')
      .concat(String(width), ' v ')
      .concat(String(height), ' h ')
      .concat(String(-width), ' Z');
  }

  return path;
};

const sanitizeSvgId = (value: string) => value.replace(/[ /\\()\-#]/g, '_');

const renderRevBarGradientShape =
  (chartId: string, key: any, lightColor: any, darkColor: any) =>
  (props: any) => {
    const { height, width, x, y } = props;
    const id = `${sanitizeSvgId(chartId)}_${sanitizeSvgId(String(key))}`;

    return (
      <svg x={x || 0} y={y || 0} fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient
            id={id}
            spreadMethod="pad"
            x1="100%"
            y1="0%"
            x2="0%"
            y2="0%"
          >
            <stop
              offset="0%"
              style={{ stopColor: lightColor, stopOpacity: 1 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: darkColor, stopOpacity: 1 }}
            />
          </linearGradient>
        </defs>
        <path
          fill={`url(#${id})`}
          width={width}
          height={height}
          d={getRectanglePath(0, 0, width || 0, height || 0, [0, 20, 20, 0])}
        />
      </svg>
    );
  };

const renderBarGradientShape =
  (chartId: string, key: any, lightColor: any, darkColor: any) =>
  (props: any) => {
    const { height, width, x, y } = props;
    const id = `${sanitizeSvgId(chartId)}_${sanitizeSvgId(String(key))}`;
    return (
      <svg x={x || 0} y={y || 0} fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient
            id={id}
            spreadMethod="pad"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop
              offset="0%"
              style={{ stopColor: lightColor, stopOpacity: 1 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: darkColor, stopOpacity: 1 }}
            />
          </linearGradient>
        </defs>
        <path
          fill={`url(#${id})`}
          width={width}
          height={height || ''}
          d={getRectanglePath(0, 0, width || 0, height || 0, [20, 20, 0, 0])}
        />
      </svg>
    );
  };

export function getAxisWidth(data: any[], titleField = '__name') {
  let max = 0;
  // Accept either a series array, or historically nested [series]
  const series = Array.isArray(data?.[0])
    ? data[0]
    : Array.isArray(data)
      ? data
      : [];
  series.forEach((d: any) => {
    const title = d?.[titleField];
    const current =
      typeof title === 'string'
        ? title.length
        : title !== null
          ? String(title).length
          : 0;
    if (current > max) max = current;
  });
  if (max > 40) max = 40;
  if (max < 10) max = 10;
  return max * 8;
}

function smartConvertValueToKMT(value: number) {
  if (Number.isNaN(value) || value === undefined || value === null) return '-';
  if (Math.abs(value) < 0.01) return convertNumbersToProperFormat(value, 5);
  if (Math.abs(value) < 1) return convertNumbersToProperFormat(value, 3);
  if (Math.abs(value) < 1000) return convertNumbersToProperFormat(value, 2);
  if (Math.abs(value) < 1000000)
    return convertNumbersToProperFormat(value / 1000, 2) + 'K';
  return convertNumbersToProperFormat(value / 1000_000, 2) + 'M';
}
const LineCustomizedLabel = (props: any) => {
  const { x, y, stroke, value } = props;
  if (!isANumber(value)) return <div />;
  return (
    <text x={x} y={y} dy={-15} fill={stroke} fontSize={12} textAnchor="middle">
      {smartConvertValueToKMT(value)}
    </text>
  );
};
const BarCustomizedLabel = (props: any) => {
  const { x, y, width, height, value, cx, cy } = props;
  // Always return a valid SVG element — returning null/string lets Recharts
  // fall through to its Text component, which can infinite-loop via setState
  // in updateWordsByLines when chart width oscillates.
  if (!isANumber(value)) return <g />;
  const hasBarGeometry =
    isANumber(x) && isANumber(y) && isANumber(width) && isANumber(height);
  const labelX = hasBarGeometry ? x + width / 2 : cx;
  const labelY = hasBarGeometry ? y + height / 2 : cy;
  if (!isANumber(labelX) || !isANumber(labelY)) return <g />;
  return (
    <text
      x={labelX}
      y={labelY}
      fill={exactWhite}
      fontSize={12}
      textAnchor="middle"
      dominantBaseline="middle"
      className="clickTrough"
    >
      {smartConvertValueToKMT(value)}
    </text>
  );
};

const PieCustomizedLabel = (props: any) => {
  const { value, x, y, fill } = props;
  if (!isANumber(x) || !isANumber(y)) return <g />;
  return (
    <text
      x={x}
      y={y}
      fill={fill || exactWhite}
      fontSize={12}
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {isANumber(value) ? smartConvertValueToKMT(value) : value}
    </text>
  );
};

const TickFormatter = (value: number) => {
  return smartConvertValueToKMT(value);
};

const TooltipFormatter = (value: any, name: string) => {
  if (isANumber(value)) return [convertNumbersToProperFormat(value, 3), name];
  return [value, name];
};
const MyTooltip = (
  <Tooltip
    formatter={TooltipFormatter}
    contentStyle={{
      borderRadius: '10px',
      boxShadow: 'rgb(216 216 216 / 33%) 1px 1px 9px 7px',
    }}
  />
);

export function getChartType(
  currSeries: ISeries,
  query: IAnalyticsQuery,
  bucket: any,
  override?: ReportOverride
) {
  let chartType = override?.chartType || currSeries?.chartType;
  if (chartType === null) {
    if (query.metrics[0].metric === 'count') chartType = 'table';
    else if (
      bucket.field === 'effectiveDate' ||
      bucket.field === 'effectiveJMonth'
    )
      chartType = 'line';
    else if (
      bucket.field === 'allAnswers' ||
      bucket.field === 'questionId' ||
      bucket.field === 'subQuestionId' ||
      bucket.field === 'instance'
    )
      chartType = 'bar';
    else chartType = 'table';
  }
  return chartType;
}

const rotateData = (data: any) => {
  const ret = data.reduce((current: any[], item: any) => {
    const edit = { ...item };
    delete edit[messages.total];

    const keys = Object.keys(item).filter((it) => !it.startsWith('__'));
    const name = item['__name'];
    keys.forEach((key) => {
      let found = current.find((it: any) => it['__name'] === key);
      if (!found) {
        found = {
          __name: key,
        };
        current.push(found);
      }
      found[name] = edit[key];
    });
    return current;
  }, []);
  return ret;
};
const extractMapData = (
  data: any[],
  queryKey: string,
  uniqueQuestionName: string[]
) => {
  return data[queryKey].reduce((current: any, item: any) => {
    const name = item['__name'];
    const valueKeys = Object.keys(item)
      ?.filter((it) => !it.startsWith('__'))
      ?.sort(
        (a, b) => uniqueQuestionName.indexOf(a) - uniqueQuestionName.indexOf(b)
      );
    if (valueKeys?.length) {
      current[name] = valueKeys.reduce((acc: any, currKey: string) => {
        acc[currKey] = item?.[currKey] || 0;
        return acc;
      }, {});
    }
    return current;
  }, {});
};
const ChartForCustomSeries: React.FC<ComponentProps> = ({
  calculateOffset,
  checkpoints,
  currSeries,
  CustomizedAxisTick,
  data,
  // handleShowCheckPoint,
  handleShowRelatedResponsesModal,
  isPrinting,
  // onChangeShowCheckPoints,
  queryKey,
  seriesItems,
  showCheckPoints,
  YAxisLabel,
  groupId,
  itemKey,
  query,
  bucket,
  compact,
  compactAutoDim,
  reportTitle,
  reportOverride,
  ref,
  report,
  simple,
  survey,
}) => {
  const uniqueQuestionIdList =
    currSeries?.questionId?.split(',')?.map((el: string) => {
      if (el.includes('@score')) {
        return el.split('@score').at(0) as string;
      } else return el;
    }) || [];
  const [uniqueQuestionName, _] = uniqueQuestionIdList
    ? getQuestionListInfo(
        data?.[queryKey] ?? data,
        survey,
        uniqueQuestionIdList
      )
    : [];

  const chartType = getChartType(currSeries, query, bucket, reportOverride);
  const color = reportOverride?.chartColor || currSeries.color;
  const gradientChartId = `${itemKey ?? report?.id ?? reportTitle}_${queryKey}`;

  function getStackHeight(data: any[]) {
    let max = 5;
    data?.forEach((it) => {
      max = Math.max(
        max,
        Object.keys(it).filter((key) => key.startsWith('__')).length
      );
    });
    if (max > 20) max = 20;
    return 350 + 20 * (max - 5);
  }

  function sortDataIfRequired(passedData?: any, isStack?: boolean) {
    const orderByValue = reportOverride?.order || currSeries.order;

    const dataQK = passedData || data[queryKey];
    if (orderByValue && orderByValue !== 'Default') {
      const seriesItemQK = passedData
        ? Object.keys(passedData[0]).filter((it) => !it.startsWith('__'))
        : seriesItems[queryKey];
      const item = isStack
        ? seriesItemQK
        : seriesItemQK?.find(
            (it: string) => it === i18n.t('total', { ns: 'survey' })
          ) || seriesItemQK?.[0];
      if (!item) return dataQK;
      return dataQK.sort((a: any, b: any) => {
        if (!isStack) {
          return (
            ((a[item as string] || Number.MIN_VALUE) -
              (b[item as string] || Number.MIN_VALUE)) *
            (orderByValue === 'ValueAsc' ? 1 : -1)
          );
        } else {
          const sumA = (item as string[])?.reduce((acc: number, el) => {
            acc = acc + (a?.[el] || 0);
            return acc;
          }, 0);
          const sumB = (item as string[])?.reduce((acc: number, el) => {
            acc = acc + (b?.[el] || 0);
            return acc;
          }, 0);
          return (
            ((sumA || Number.MIN_VALUE) - (sumB || Number.MIN_VALUE)) *
            (orderByValue === 'ValueAsc' ? 1 : -1)
          );
        }
      });
    }
    return dataQK;
  }

  return (
    <>
      {chartType === 'table' || chartType === 'tableRev' ? (
        <HeatChart
          simple={simple}
          showPercentage={showCheckPoints}
          data={sortDataIfRequired()}
          checkpoints={checkpoints}
          series={seriesItems[queryKey]}
          groupId={groupId}
          itemKey={itemKey}
          color={color}
          onClickOnCell={
            !simple
              ? handleShowRelatedResponsesModal(queryKey, true)
              : () => {
                  /*do nothing*/
                }
          }
          isPrinting={isPrinting}
          reverse={chartType === 'tableRev'}
          reportTitle={reportTitle}
          ref={ref as any}
          report={report}
          compact={compact}
        />
      ) : chartType === 'iranMap' ? (
        <IranMapWithBadge
          mapData={extractMapData(data, queryKey, uniqueQuestionName)}
          tooltipTitle={reportTitle}
          color={color}
          onClickOverProvince={(province: string) => {
            if (!simple) {
              const id = data[queryKey].find(
                (el: any) => el['__name'] === province
              )?.__id;
              handleShowRelatedResponsesModal(queryKey, false)(id);
            }
          }}
        />
      ) : chartType === 'line' && data[queryKey].length > 1 ? (
        <React.Fragment>
          {/*{onChangeShowCheckPoints && !compact && (
            <Input
              type="checkbox"
              id="showCheckPoints"
              onChange={handleShowCheckPoint}
              checked={showCheckPoints}
              label={messages.showCheckPoints}
            />
          )}*/}
          <div style={{ direction: 'ltr', overflow: 'hidden' }}>
            <ResponsiveContainer
              width="100%"
              height={(report?.dashboardHeight || 2) * 200 - 80}
            >
              <ComposedChartWithStyle
                data={sortDataIfRequired()}
                margin={marginForChart}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="__name"
                  tick={<CustomizedAxisTick />}
                  interval={0}
                  tickSize={20}
                  height={getAxisWidth(data[queryKey]) / 1.3}
                />
                <YAxis
                  yAxisId="left"
                  tickFormatter={TickFormatter}
                  label={{
                    value: YAxisLabel[queryKey],
                    angle: -90,
                    position: 'insideLeft',
                  }}
                />
                {showCheckPoints && (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    domain={[-100, 100]}
                    tickFormatter={(value) => value + '%'}
                    label={{
                      value: i18n.t('percentageOfRealization', {
                        ns: 'survey',
                      }) as string,
                      angle: 90,
                      position: 'insideRight',
                    }}
                  />
                )}
                {!compact && <Legend verticalAlign="top" />}
                {!isPrinting && MyTooltip}
                {showCheckPoints && (
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="__baseLine"
                    stroke="#f23e3e"
                    strokeDasharray="5 5"
                    strokeWidth={4}
                    name={i18n.t('baseLine', { ns: 'survey' }) as string}
                    activeDot={{
                      onClick: !simple
                        ? handleShowRelatedResponsesModal(queryKey, false)
                        : () => {
                            /*do nothing*/
                          },
                    }}
                    label={isPrinting ? <LineCustomizedLabel /> : undefined}
                  />
                )}
                {showCheckPoints && (
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="__target"
                    stroke="#0bbc78"
                    strokeDasharray="5 5"
                    strokeWidth={4}
                    name={i18n.t('target', { ns: 'survey' }) as string}
                    label={isPrinting ? <LineCustomizedLabel /> : undefined}
                  />
                )}
                <defs>
                  <linearGradient id="splitColor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="green" stopOpacity={1} />
                    <stop
                      offset={calculateOffset(data[queryKey])}
                      stopColor="white"
                      stopOpacity={0}
                    />
                    <stop offset="100%" stopColor="red" stopOpacity={1} />
                  </linearGradient>
                </defs>
                {showCheckPoints && (
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="__percentageOfRealization"
                    stroke="#555"
                    fill="url(#splitColor)"
                    name={
                      i18n.t('percentageOfRealization', {
                        ns: 'survey',
                      }) as string
                    }
                  />
                )}
                {seriesItems[queryKey].sort().map((item: string, _, array) => (
                  <Line
                    key={item}
                    name={item}
                    dataKey={item}
                    type="monotone"
                    yAxisId="left"
                    stroke={
                      report?.colorMap?.[item] ??
                      (array.length < 2 && color
                        ? color || stringToColour(item)
                        : stringToColour(item))
                    }
                    strokeWidth={2}
                    isAnimationActive={true}
                    activeDot={{
                      onClick: !simple
                        ? handleShowRelatedResponsesModal(queryKey, false)
                        : () => {
                            /*do nothing*/
                          },
                    }}
                    label={isPrinting ? <LineCustomizedLabel /> : undefined}
                  />
                ))}
              </ComposedChartWithStyle>
            </ResponsiveContainer>
          </div>
        </React.Fragment>
      ) : chartType === 'barRev' ? (
        <div style={{ direction: 'ltr' }}>
          <ResponsiveContainer
            width="100%"
            height={getBarChartHeight(data[queryKey])}
          >
            <BarChart
              data={sortDataIfRequired()}
              margin={marginForChart}
              layout="vertical"
            >
              {!compact && <CartesianGrid strokeDasharray="3 3" />}

              <YAxis
                dataKey="__name"
                interval={0}
                type="category"
                width={getAxisWidth(data[queryKey])}
              />

              <XAxis type="number" tickFormatter={TickFormatter} />

              {!compact && <Legend verticalAlign="top" />}
              {!isPrinting && MyTooltip}
              {seriesItems[queryKey].map((item: string, _, array) => {
                return (
                  <Bar
                    key={item}
                    name={item}
                    dataKey={item}
                    fill={
                      array.length < 2 && color ? color : stringToColour(item)
                    }
                    shape={renderRevBarGradientShape(
                      gradientChartId,
                      item,
                      array.length < 2 && color
                        ? color
                        : stringToColour(item as string, 1, false),
                      array.length < 2 && color
                        ? lightenHexHsl(color, 0.2)
                        : stringToColour(item as string, 1, true)
                    )}
                    isAnimationActive={true}
                    radius={[0, 20, 20, 0]}
                    onClick={
                      !simple
                        ? handleShowRelatedResponsesModal(queryKey, true)
                        : () => {
                            /*do nothing*/
                          }
                    }
                  >
                    {seriesItems[queryKey].length === 1 && (
                      <LabelList
                        dataKey={item}
                        position="center"
                        className="clickTrough"
                        content={BarCustomizedLabel}
                      />
                    )}
                    {seriesItems[queryKey].length === 1 &&
                      data[queryKey].map(
                        (entry: any, index: any, array2: any[]) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              report?.colorMap?.[entry['__name']] ??
                              (array2.length < 2 && color
                                ? color
                                : stringToColour(entry['__name']))
                            }
                          />
                        )
                      )}
                  </Bar>
                );
              })}
              {Object.keys(data[queryKey][0] || {}).filter(
                (it) => !it.startsWith('__')
              ).length > 1 && <Legend verticalAlign="top" />}
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : chartType === 'bar' || chartType === 'line' ? (
        <div
          style={{ direction: 'ltr', overflowX: 'scroll', overflowY: 'hidden' }}
        >
          {!compact && (
            <div style={{ direction: 'rtl' }}>{YAxisLabel[queryKey]}</div>
          )}

          <ResponsiveContainer
            width="100%"
            minWidth={data[queryKey].length * 25 + 20}
            height={
              compact && compactAutoDim ? compactAutoDim[1] * 200 - 120 : 550
            }
          >
            <ComposedChartWithStyle
              data={sortDataIfRequired()}
              margin={marginForChart}
            >
              {!compact && <CartesianGrid strokeDasharray="3 3" />}
              <XAxis
                dataKey="__name"
                tick={<CustomizedAxisTick />}
                interval={0}
                tickSize={20}
                height={getAxisWidth(data[queryKey]) / 1.3}
              />
              <YAxis
                label={
                  !compact
                    ? {
                        value: YAxisLabel[queryKey],
                        angle: -90,
                        position: 'insideLeft',
                      }
                    : undefined
                }
                width={compact ? 30 : 60}
                tickFormatter={TickFormatter}
              />
              {!compact ||
                (seriesItems[queryKey].length > 1 && (
                  <Legend verticalAlign="top" />
                ))}
              {!isPrinting && MyTooltip}
              {seriesItems?.[queryKey]?.map((item: string, _) => (
                <Bar
                  key={item}
                  name={item}
                  dataKey={item}
                  fill={
                    report?.colorMap?.[item] ??
                    (color ? color : stringToColour(item as string))
                  }
                  isAnimationActive={true}
                  shape={renderBarGradientShape(
                    gradientChartId,
                    item,
                    color ? color : stringToColour(item as string, 1, false),
                    color
                      ? lightenHexHsl(color, 0.2)
                      : stringToColour(item as string, 1, true)
                  )}
                  radius={[20, 20, 0, 0]}
                  onClick={
                    !simple
                      ? handleShowRelatedResponsesModal(queryKey, false)
                      : () => {
                          /*do nothing*/
                        }
                  }
                >
                  <LabelList
                    dataKey={item}
                    position="center"
                    className="clickTrough"
                    content={BarCustomizedLabel}
                  />
                  {seriesItems[queryKey].length === 1 &&
                    data[queryKey].map((entry: any, index: any, _: any[]) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          report?.colorMap?.[entry['__name']] ??
                          (color ? color : stringToColour(entry['__name']))
                        }
                      />
                    ))}
                </Bar>
              ))}
              {Object.keys(data[queryKey][0] || {}).filter(
                (it) => !it.startsWith('__')
              ).length > 1 && <Legend verticalAlign="top" />}
              {showCheckPoints && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={['dataMin', 100]}
                  tickFormatter={(value) => value + '%'}
                  label={{
                    value: i18n.t('percentageOfRealization', {
                      ns: 'survey',
                    }) as string,
                    angle: 90,
                    position: 'insideRight',
                  }}
                />
              )}
              {showCheckPoints && (
                <Line
                  type="monotone"
                  dataKey="__target"
                  stroke="#0bbc78"
                  strokeDasharray="5 5"
                  strokeWidth={4}
                  name={
                    i18n.t('target', {
                      ns: 'survey',
                    }) as string
                  }
                  label={isPrinting ? <LineCustomizedLabel /> : undefined}
                />
              )}
              {showCheckPoints && (
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="__percentageOfRealization"
                  stroke="#555"
                  fill="url(#splitColor)"
                  name={
                    i18n.t('percentageOfRealization', {
                      ns: 'survey',
                    }) as string
                  }
                />
              )}
              {showCheckPoints && (
                <Line
                  type="monotone"
                  dataKey="__baseLine"
                  stroke="#f23e3e"
                  strokeDasharray="5 5"
                  strokeWidth={4}
                  name={
                    i18n.t('baseLine', {
                      ns: 'survey',
                    }) as string
                  }
                  activeDot={{
                    onClick: !simple
                      ? handleShowRelatedResponsesModal(queryKey, false)
                      : () => {
                          /*do nothing*/
                        },
                  }}
                  label={isPrinting ? <LineCustomizedLabel /> : undefined}
                />
              )}
            </ComposedChartWithStyle>
          </ResponsiveContainer>
        </div>
      ) : chartType === 'stack' ? (
        <div style={{ direction: 'ltr' }}>
          <ResponsiveContainer
            width="100%"
            height={getStackHeight(data[queryKey])}
          >
            <BarChart
              data={sortDataIfRequired(undefined, true).map((it: any) => {
                const ret = { ...it };
                delete ret[messages.total];
                return ret;
              })}
              margin={marginForChart}
            >
              {!compact && <CartesianGrid strokeDasharray="3 3" />}

              <XAxis
                dataKey="__name"
                tick={<CustomizedAxisTick />}
                interval={0}
                tickSize={20}
                height={getAxisWidth(data[queryKey]) / 1.3}
              />

              <YAxis
                label={
                  !compact
                    ? {
                        value: YAxisLabel[queryKey],
                        angle: -90,
                        position: 'insideLeft',
                      }
                    : undefined
                }
                tickFormatter={TickFormatter}
              />

              {!compact && <Legend verticalAlign="top" />}
              {!isPrinting && MyTooltip}
              {seriesItems[queryKey].map((item: string, _, array) => (
                <Bar
                  key={item}
                  name={item}
                  dataKey={item}
                  fill={
                    report?.colorMap?.[item] ??
                    (array.length < 2 && color
                      ? color
                      : stringToColour(item as string))
                  }
                  isAnimationActive={true}
                  stackId="s1"
                  onClick={
                    !simple
                      ? handleShowRelatedResponsesModal(queryKey, false)
                      : () => {
                          /*do nothing*/
                        }
                  }
                >
                  {/*uncomment next block to show labels on chart*/}
                  {/*<LabelList
                    dataKey={item}
                    position="center"
                    className="black-fill clickTrough"
                    content={BarCustomizedLabel}
                  />*/}
                  {seriesItems[queryKey].length === 1 &&
                    data[queryKey].map(
                      (entry: any, index: any, array2: any[]) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            array2.length < 2 && color
                              ? color
                              : stringToColour(entry['__name'])
                          }
                        />
                      )
                    )}
                </Bar>
              ))}
              {Object.keys(data[queryKey][0] || {}).filter(
                (it) => !it.startsWith('__')
              ).length > 1 && <Legend verticalAlign="top" />}
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : chartType === 'stackRev' ? (
        (() => {
          const rotateData1 = sortDataIfRequired(
            rotateData(data[queryKey]),
            true
          );
          return (
            <div style={{ direction: 'ltr' }}>
              <ResponsiveContainer
                width="100%"
                height={getStackHeight(data[queryKey])}
              >
                <BarChart data={rotateData1} margin={marginForChart}>
                  {!compact && <CartesianGrid strokeDasharray="3 3" />}

                  <XAxis
                    dataKey="__name"
                    tick={<CustomizedAxisTick />}
                    interval={0}
                    tickSize={20}
                    height={getAxisWidth(rotateData1) / 1.3}
                  />

                  <YAxis
                    label={
                      !compact
                        ? {
                            value: YAxisLabel[queryKey],
                            angle: -90,
                            position: 'insideLeft',
                          }
                        : undefined
                    }
                    tickFormatter={TickFormatter}
                  />

                  {!compact && <Legend verticalAlign="top" />}
                  {!isPrinting && MyTooltip}
                  {data[queryKey]
                    .map((it: any) => it['__name'])
                    .map((item: string, _: any, array: any[]) => (
                      <Bar
                        key={item}
                        name={item}
                        dataKey={item}
                        fill={
                          array.length < 2 && color
                            ? color
                            : stringToColour(item as string)
                        }
                        isAnimationActive={true}
                        stackId="s1"
                        onClick={
                          !simple
                            ? handleShowRelatedResponsesModal(queryKey, false)
                            : () => {
                                /*do nothing*/
                              }
                        }
                      >
                        {/*uncomment next block to show labels on chart*/}
                        {/*<LabelList
                          dataKey={item}
                          position="center"
                          className="black-fill clickTrough"
                          content={BarCustomizedLabel}
                        />*/}
                        {seriesItems[queryKey].length === 1 &&
                          data[queryKey].map(
                            (entry: any, index: any, array2: number[]) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={
                                  report?.colorMap?.[entry['__name']] ??
                                  (array2.length < 2 && color
                                    ? color
                                    : stringToColour(entry['__name']))
                                }
                              />
                            )
                          )}
                      </Bar>
                    ))}
                  {Object.keys(data[queryKey][0] || {}).filter(
                    (it) => !it.startsWith('__')
                  ).length > 1 && <Legend verticalAlign="top" />}
                </BarChart>
              </ResponsiveContainer>
            </div>
          );
        })()
      ) : chartType === 'pie' ? (
        <div style={{ direction: 'ltr', overflow: 'hidden' }}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart data={sortDataIfRequired()} margin={marginForChart}>
              {seriesItems[queryKey].length <= 1 && (
                <Legend verticalAlign="top" />
              )}
              {!isPrinting && MyTooltip}
              {seriesItems[queryKey].map((item: string, index, all) => {
                const partSize = 220 / 2 / all.length;
                return (
                  <Pie
                    key={item}
                    data={data[queryKey]}
                    dataKey={item}
                    nameKey="__name"
                    /*
                    fill={stringToColour(item as string)}
*/
                    cx="50%"
                    cy="50%"
                    innerRadius={index * partSize + 3}
                    outerRadius={(index + 1) * partSize - 3}
                    isAnimationActive={true}
                    label={
                      index === all.length - 1 ? PieCustomizedLabel : false
                    }
                    onClick={
                      !simple
                        ? handleShowRelatedResponsesModal(queryKey, false)
                        : () => {
                            //console.log()
                          }
                    }
                  >
                    <LabelList
                      dataKey={item}
                      position="center"
                      className="clickTrough"
                      content={BarCustomizedLabel}
                    />
                    {data[queryKey].map(
                      (entry: any, index: any, array: any[]) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            report?.colorMap?.[entry['__name']] ??
                            (array.length < 2 && color
                              ? color
                              : stringToColour(entry['__name']))
                          }
                        />
                      )
                    )}
                  </Pie>
                );
              })}
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </>
  );
};

export default React.memo(ChartForCustomSeries);
